import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import useStore from '../../store'
import { goalFlags } from '../../lib/constraints'
import { CONFIG } from '../../config/config'

const CATEGORIES = ['Career','Health','Relationships','Finances','Personal Growth','Creative','Education','Other']
const EFFORT = [[1,'Low','Routine, low mental load'],[2,'Medium','Requires consistent focus'],[3,'High','Deep work, high intensity']]

export default function GoalForm({ existing, defaultBucket = 'aspirational', onDone }) {
  const createGoal   = useStore(s => s.createGoal)
  const updateGoal   = useStore(s => s.updateGoal)
  const afterMutation = useStore(s => s.afterMutation)
  const profile      = useStore(s => s.profile)
  const has          = useStore(s => s.has)

  const [f, setF] = useState({
    title:           existing?.title           ?? '',
    description:     existing?.description     ?? '',
    category:        existing?.category        ?? '',
    bucket:          existing?.bucket          ?? defaultBucket,
    effort_level:    existing?.effort_level    ?? 1,
    estimated_weeks: existing?.estimated_weeks ?? '',
    weekly_hours:    existing?.weekly_hours    ?? '',
    cost_estimate:   existing?.cost_estimate   ?? '',
  })
  const [busy, setBusy] = useState(false)
  const [err,  setErr]  = useState('')
  const [flags, setFlags] = useState([])

  const upd = (k, v) => setF(p => ({ ...p, [k]: v }))

  useEffect(() => {
    if (f.estimated_weeks && f.weekly_hours) {
      setFlags(goalFlags(
        { estimated_weeks: Number(f.estimated_weeks), weekly_hours: Number(f.weekly_hours), effort_level: f.effort_level },
        profile?.weekly_available_hours ?? 0
      ))
    } else setFlags([])
  }, [f.estimated_weeks, f.weekly_hours, f.effort_level, profile])

  const submit = async () => {
    if (!f.title.trim()) return setErr('Title is required.')
    setBusy(true); setErr('')

    const payload = {
      ...f,
      estimated_weeks: f.estimated_weeks ? Number(f.estimated_weeks) : null,
      weekly_hours:    f.weekly_hours    ? Number(f.weekly_hours)    : null,
      cost_estimate:   f.cost_estimate   ? Number(f.cost_estimate)   : null,
    }

    const { error } = existing
      ? await updateGoal(existing.id, payload)
      : await createGoal(payload)

    setBusy(false)
    if (error) return setErr(error.message ?? 'Something went wrong')
    await afterMutation()
    onDone?.()
  }

  const totalHours = f.estimated_weeks && f.weekly_hours
    ? (Number(f.estimated_weeks) * Number(f.weekly_hours)).toFixed(0)
    : null

  return (
    <div className="flex flex-col gap-4">

      {/* Title */}
      <div>
        <label className="label block mb-1.5">Title *</label>
        <input className="input" placeholder="What do you want to achieve?" value={f.title}
          onChange={e => upd('title', e.target.value)} autoFocus />
      </div>

      {/* Description */}
      <div>
        <label className="label block mb-1.5">Description</label>
        <textarea className="textarea" rows={3} placeholder="Why does this matter? What does done look like?"
          value={f.description} onChange={e => upd('description', e.target.value)} />
      </div>

      {/* Category + Bucket */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="label block mb-1.5">Category</label>
          <select className="input" value={f.category} onChange={e => upd('category', e.target.value)}>
            <option value="">Select…</option>
            {CATEGORIES.map(c => <option key={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <label className="label block mb-1.5">Bucket</label>
          <div className="flex gap-2 h-[38px]">
            {['aspirational','committed'].map(b => (
              <button key={b} onClick={() => upd('bucket', b)}
                className={`flex-1 btn text-[10px] py-0 ${f.bucket === b ? 'border-m-accent text-m-accent bg-m-accent/5' : 'border-m-border text-m-subtle'}`}>
                {b}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Cognitive load */}
      <div>
        <label className="label block mb-1.5">Cognitive load</label>
        <div className="flex gap-2">
          {EFFORT.map(([v, l]) => (
            <button key={v} onClick={() => upd('effort_level', v)}
              className={`flex-1 btn text-[10px] py-2 ${
                f.effort_level === v
                  ? v === 1 ? 'border-m-faint text-m-subtle bg-m-muted/40'
                  : v === 2 ? 'border-m-warn/60 text-m-warn bg-m-warn/5'
                  :           'border-m-danger/60 text-m-danger bg-m-danger/5'
                  : 'border-m-border text-m-faint'
              }`}>
              {l}
            </button>
          ))}
        </div>
      </div>

      {/* Time inputs */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="label block mb-1.5">Duration (weeks)</label>
          <input type="number" min="1" className="input" placeholder="e.g. 12"
            value={f.estimated_weeks} onChange={e => upd('estimated_weeks', e.target.value)} />
        </div>
        <div>
          <label className="label block mb-1.5">Hours / week</label>
          <input type="number" min="0.5" step="0.5" className="input" placeholder="e.g. 5"
            value={f.weekly_hours} onChange={e => upd('weekly_hours', e.target.value)} />
        </div>
      </div>

      {/* Total hours */}
      {totalHours && (
        <div className="alert-info">
          Total estimated effort: <strong className="font-mono text-m-text">{totalHours}h</strong>
        </div>
      )}

      {/* Cost (unlocked) */}
      {has('cost_field') && (
        <div>
          <label className="label block mb-1.5">Estimated cost</label>
          <input type="number" min="0" className="input" placeholder="0"
            value={f.cost_estimate} onChange={e => upd('cost_estimate', e.target.value)} />
        </div>
      )}

      {/* Flags */}
      {flags.map((fl, i) => <div key={i} className="alert-warn">{fl.msg}</div>)}

      {err && <div className="alert-danger">{err}</div>}

      <div className="flex gap-2 pt-1">
        <button onClick={submit} disabled={busy} className="btn-primary flex-1">
          {busy ? 'Saving…' : existing ? 'Update' : 'Create goal'}
        </button>
        {onDone && <button onClick={onDone} className="btn-ghost">Cancel</button>}
      </div>
    </div>
  )
}
